import { Box, Table, TableContainer, Tbody, Td, Tr } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RanaLoading } from "@nens/ranalib";
import { capitalizeFirst } from "@nens/shared-utils";

interface PopupContentProps {
  isLoading?: boolean;
  isError?: boolean;
  label?: string;
  value?: ReactNode;
}

export function PopupContent({ children }: { children: ReactNode }) {
  return (
    <TableContainer>
      <Table
        layout="fixed"
        variant="unstyled"
        color="brand.clear_creek"
        overflow="hidden"
        textOverflow="ellipsis"
        fontWeight={700}
        w=""
      >
        <Tbody>{children}</Tbody>
      </Table>
    </TableContainer>
  );
}

export function PopupContentItem(props: PopupContentProps) {
  const { isLoading, isError, label, value } = props;

  const renderValue = () => {
    if (isError) {
      return <FormattedMessage id="popup.failed" defaultMessage="Failed" />;
    }
    if (value === undefined) {
      return <FormattedMessage id="popup.no_value" defaultMessage="No value" />;
    }

    // If value is a number, format it to 2 decimals
    if (!Number.isNaN(Number(value))) {
      return +Number(value).toFixed(2);
    }

    return value;
  };

  const renderLabel = () => {
    if (label === undefined) {
      return <FormattedMessage id="popup.value" defaultMessage="Value" />;
    }

    const cleanLabel = label.replace(/_/g, " ");
    return capitalizeFirst(cleanLabel);
  };

  return (
    <Tr key={label} justifyContent="space-between" whiteSpace="nowrap" alignItems="center" w="fit">
      <Td
        fontWeight={700}
        color="brand.analytic"
        overflow="hidden"
        textOverflow="ellipsis"
        textStyle="xs"
        title={label}
        p={0}
      >
        {renderLabel()}
      </Td>
      <Td p={0} pl={2}>
        {isLoading ? (
          <RanaLoading mb={1} boxSize={4} />
        ) : (
          <Box
            mb={1}
            whiteSpace="pre-wrap"
            color="brand.deep_sea"
            fontWeight={300}
            overflow="hidden"
            textOverflow="ellipsis"
            title={label}
          >
            {renderValue()}
          </Box>
        )}
      </Td>
    </Tr>
  );
}

PopupContent.Item = PopupContentItem;
